import type { SmsInvite } from "@rhema/client";
import { CopyIcon, cn, focusRing, MailIcon, MessageIcon, MoreIcon, SendIcon } from "@rhema/ui";
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderSVG } from "uqr";
import { useFeatures } from "../session/features";
import { useApi, useSession } from "../session/session";
import { lastTextNote, waitNote } from "./smsInviteText";

/** What a share did, for the sheet that holds the buttons. */
export type ShareResult = { done: boolean; note?: string };

function WhatsAppMark() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        fill="#25D366"
        d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.1.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.5-.3Z"
      />
    </svg>
  );
}

function TelegramMark() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#2AABEE" />
      <path
        fill="#fff"
        d="m6.8 11.7 9.4-3.6c.4-.2.8.1.7.7l-1.6 7.5c-.1.5-.4.7-.9.4l-2.4-1.8-1.2 1.1c-.1.1-.3.2-.5.2l.2-2.5 4.5-4.1c.2-.2 0-.3-.3-.1l-5.6 3.5-2.4-.7c-.5-.2-.5-.5.1-.7Z"
      />
    </svg>
  );
}

function Target({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-field px-1 py-2.5 text-[12.5px] font-semibold text-ink hover:bg-surface-header",
          focusRing,
        )}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-header">
          {icon}
        </span>
        <span className="text-center leading-tight">{label}</span>
      </button>
    </li>
  );
}

/**
 * Ways to send a message and link from any device: WhatsApp, a text, email and Telegram, the
 * device's own share menu where it has one, copying, and a QR code. `phone` is who the text goes to.
 */
export function ShareTargets({
  text,
  url,
  subject = "Rhema",
  phone,
  qrCode = true,
  onResult,
}: {
  text: string;
  url?: string;
  subject?: string;
  phone?: string;
  /** Whether to offer a QR code of the link. Off where the screen already shows one. */
  qrCode?: boolean;
  onResult: (result: ShareResult) => void;
}) {
  const { platform } = useSession();
  const api = useApi();
  const features = useFeatures();
  const [qr, setQr] = useState(false);
  const enc = encodeURIComponent;
  const full = url && !text.includes(url) ? `${text} ${url}` : text;
  const phoneApp = platform.kind === "capacitor";
  // Whether the device has its own share menu. The desktop app on Linux has none.
  const systemShare = platform.hasShareSheet();
  // Desktops cannot text. The server can, when it is set up to.
  const serverText = !phoneApp && Boolean(phone) && features?.sms_invites === true;
  // The last text the server sent to this number for you, so the sheet can say when it went.
  const [lastText, setLastText] = useState<SmsInvite | null>(null);
  const sending = useRef(false);

  const loadLastText = useCallback(async () => {
    if (!serverText || !phone) return;
    const answer = await api.lastSmsInvite(phone).catch(() => null);
    if (answer) setLastText(answer.last);
  }, [api, phone, serverText]);

  useEffect(() => {
    setLastText(null);
    void loadLastText();
  }, [loadLastText]);

  const sendText = async () => {
    // A second click while the first is still going sends nothing.
    if (sending.current) return;
    sending.current = true;
    try {
      await api.sendSmsInvite(phone ?? "");
      onResult({ done: true, note: "Rhema texted the invite." });
      void loadLastText();
    } catch (e) {
      onResult({ done: false, note: waitNote(e) ?? (e as Error).message });
    } finally {
      sending.current = false;
    }
  };

  /** Opens another app, without leaving Rhema on the web. */
  const open = (link: string) => {
    if (platform.native) return void platform.openExternal(link).catch(() => undefined);
    if (/^(sms|mailto):/.test(link)) window.location.href = link;
    else window.open(link, "_blank", "noopener");
  };
  const qrSvg = useMemo(() => (qr && url ? renderSVG(url, { border: 1 }) : ""), [qr, url]);

  return (
    <div>
      {serverText && lastText && (
        <p
          className={cn(
            "m-0 mb-3 text-[13px]",
            lastText.delivery === "failed" ? "font-semibold text-scarlet" : "text-ink-secondary",
          )}
        >
          {lastTextNote(lastText)}
        </p>
      )}
      <ul aria-label="Share with" className="m-0 grid list-none grid-cols-4 gap-1 p-0">
        <Target
          label="WhatsApp"
          icon={<WhatsAppMark />}
          onClick={() => {
            open(`https://wa.me/?text=${enc(full)}`);
            onResult({ done: true });
          }}
        />
        {serverText ? (
          <Target
            label={lastText ? "Send another text" : "Send a text"}
            icon={<MessageIcon size={20} />}
            onClick={() => void sendText()}
          />
        ) : (
          <Target
            label="Text message"
            icon={<MessageIcon size={20} />}
            onClick={() => {
              open(`sms:${phone ?? ""}?&body=${enc(full)}`);
              onResult({ done: true });
            }}
          />
        )}
        <Target
          label="Email"
          icon={<MailIcon size={20} />}
          onClick={() => {
            open(`mailto:?subject=${enc(subject)}&body=${enc(full)}`);
            onResult({ done: true });
          }}
        />
        <Target
          label="Telegram"
          icon={<TelegramMark />}
          onClick={() => {
            open(
              `https://t.me/share/url?url=${enc(url ?? "")}&text=${enc(url ? text.replace(url, "").trim() : text)}`,
            );
            onResult({ done: true });
          }}
        />
        <Target
          label="Copy"
          icon={<CopyIcon size={20} />}
          onClick={() =>
            void navigator.clipboard
              ?.writeText(full)
              .then(() => onResult({ done: false, note: "Copied. Paste it into any app." }))
              .catch(() =>
                onResult({ done: false, note: "This device would not copy. Select the text instead." }),
              )
          }
        />
        {url && qrCode && (
          <Target label="QR code" icon={<SendIcon size={18} />} onClick={() => setQr((on) => !on)} />
        )}
        {systemShare && (
          <Target
            label="More"
            icon={<MoreIcon size={20} />}
            onClick={(event) => {
              // The desktop share menu opens next to this button.
              const box = event.currentTarget.getBoundingClientRect();
              const from = { x: box.left, y: box.top, width: box.width, height: box.height };
              void platform
                .share(text, url, { title: subject, from })
                .then((outcome) => {
                  if (outcome === "shared") onResult({ done: true });
                  else if (outcome === "copied")
                    onResult({ done: false, note: "Copied. Paste it into any app." });
                })
                .catch(() => undefined);
            }}
          />
        )}
      </ul>
      {qr && qrSvg && (
        <div className="mt-3 flex flex-col items-center gap-2">
          <div
            role="img"
            aria-label="QR code for the link"
            className="h-48 w-48 rounded-card bg-white p-2"
            // The SVG is generated here from the link, not taken from anywhere else.
            // biome-ignore lint/security/noDangerouslySetInnerHtml: uqr's own SVG output
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
          <p className="m-0 text-[13px] text-ink-secondary">Scan it with a phone's camera.</p>
        </div>
      )}
    </div>
  );
}
